// Compartir el informe con colegas: por mail, en PDF o como texto para WhatsApp.
import React, { useEffect, useMemo, useState } from 'react';
import { loadDays, listDays, addDays, dayKey, dayStartMs, periodKpis, inWork, fmtKey } from './iaData';
import { diagnose, whatsappSummary } from './insights';
import { reportHtml } from './report';
import { T, Btn, inputStyle } from './ui';

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
// El envío directo (con el informe armado) por ahora existe solo en el servidor local de desarrollo.
// Publicado, se abre el correo de la persona con el resumen escrito y se adjunta el PDF.
const CAN_SEND = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

export default function ShareModal({ cam, customerId, cfg, name, period, onClose, feedId }) {
  const fromKey = dayKey(period.from), toKey = dayKey(period.to - 1);
  const span = listDays(fromKey, toKey).length;
  const prevFrom = addDays(fromKey, -span), prevTo = addDays(fromKey, -1);
  const [data, setData] = useState(null);
  const [to, setTo] = useState('');
  const [sender, setSender] = useState(() => { try { return localStorage.getItem('iaDash:remitente') || ''; } catch (e) { return ''; } });
  const [note, setNote] = useState('');
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    loadDays(cam, customerId, listDays(prevFrom, toKey), null, { feedId }).then((ph) => {
      if (!alive) return;
      const inR = (a, b) => ph.filter((p) => p.ms >= dayStartMs(a) && p.ms < dayStartMs(addDays(b, 1)) && inWork(p.ms, cfg));
      const now = inR(fromKey, toKey), prev = inR(prevFrom, prevTo);
      setData({ k: periodKpis(now, cfg), k1: prev.length ? periodKpis(prev, cfg) : null, items: diagnose(now, cfg, fromKey, toKey) });
    });
    return () => { alive = false; };
  }, [cam, customerId, cfg, fromKey, toKey, prevFrom, prevTo]);

  const rangeLabel = `${fmtKey(fromKey)} → ${fmtKey(toKey)}`;
  const prevLabel = `${fmtKey(prevFrom)} → ${fmtKey(prevTo)}`;
  const html = useMemo(() => (data ? reportHtml({ name, rangeLabel, prevLabel, ...data, fromKey, toKey, note, sender }) : ''), [data, name, rangeLabel, prevLabel, fromKey, toKey, note, sender]);
  const emails = to.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
  const bad = emails.filter((e) => !EMAIL.test(e));

  const mailto = () => {
    const txt = whatsappSummary(name, rangeLabel, data.k, data.items).replace(/[*_]/g, '');
    const body = [note, txt, '(Te adjunto el informe completo en PDF.)', sender].filter(Boolean).join('\n\n');
    window.location.href = `mailto:${emails.join(',')}?subject=${encodeURIComponent(`Informe de obra · ${name} · ${rangeLabel}`)}&body=${encodeURIComponent(body)}`;
  };

  const send = async () => {
    if (!CAN_SEND) { mailto(); return; }
    if (!emails.length || bad.length) return;
    try { localStorage.setItem('iaDash:remitente', sender); } catch (e) { /* sin storage */ }
    setState({ sending: true });
    try {
      const r = await fetch('/api/informe-ia/enviar', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ to: emails, subject: `Informe de obra · ${name} · ${rangeLabel}`, html, replyName: sender }),
      });
      const j = await r.json().catch(() => ({}));
      setState(r.ok && j.ok ? { ok: `Enviado a ${emails.join(', ')}` } : { err: j.error || 'No se pudo enviar' });
    } catch (e) { setState({ err: 'No se pudo enviar' }); }
  };

  const pdf = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const [copied, setCopied] = useState(false);
  const copyWa = async () => {
    const txt = whatsappSummary(name, rangeLabel, data.k, data.items);
    try { await navigator.clipboard.writeText(txt); } catch (e) { /* sin portapapeles */ }
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 1000, display: 'grid', placeItems: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, boxShadow: '0 25px 50px rgba(0,0,0,.25)', width: 'min(1150px, 100%)', maxHeight: '94vh', display: 'flex', flexDirection: 'column', color: T.text }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.line}`, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>Compartir informe</h3>
            <div style={{ fontSize: 13, color: T.muted }}>{rangeLabel}, comparado con los {span} días anteriores. Lo que ves acá es lo que reciben.</div>
          </div>
          <Btn onClick={onClose}>Cerrar</Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(280px, 1fr)', gap: 16, padding: 16, overflow: 'auto' }} className="iad-share-grid">
          <div style={{ border: `1px solid ${T.line}`, borderRadius: 12, overflow: 'hidden', minHeight: 420, background: T.bg }}>
            {html ? <iframe title="Vista previa del informe" srcDoc={html} style={{ width: '100%', height: '68vh', border: 'none' }} /> : <div style={{ padding: 30, color: T.muted }}>Armando el informe…</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <b style={{ fontSize: 14 }}>✉️ Enviar por mail</b>
              <label>Para (podés poner varios, separados por coma)
                <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="arquitecto@estudio.com, socio@obra.com" style={{ ...inputStyle, width: '100%', marginTop: 4 }} />
              </label>
              {bad.length > 0 && <span style={{ color: T.red }}>Revisá: {bad.join(', ')}</span>}
              <label>Tu nombre
                <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="Para que sepan quién lo manda" style={{ ...inputStyle, width: '100%', marginTop: 4 }} />
              </label>
              <label>Mensaje (opcional)
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ej.: Miren el arranque de esta semana, lo vemos el lunes." style={{ ...inputStyle, width: '100%', marginTop: 4, resize: 'vertical' }} />
              </label>
              <Btn active disabled={!data || !emails.length || bad.length > 0 || state?.sending} onClick={send}>{state?.sending ? 'Enviando…' : CAN_SEND ? 'Enviar informe' : 'Abrir en mi correo'}</Btn>
              {!CAN_SEND && <span style={{ color: T.muted, fontSize: 12 }}>Se abre tu correo con el resumen escrito. Descargá el PDF (abajo) y adjuntalo.</span>}
              {state?.ok && <span style={{ color: T.green, fontWeight: 600 }}>✓ {state.ok}</span>}
              {state?.err && <span style={{ color: T.red }}>{state.err}</span>}
            </div>
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <b style={{ fontSize: 14 }}>Otras formas</b>
              <Btn disabled={!data} onClick={pdf}>📄 Descargar PDF</Btn>
              <Btn disabled={!data} onClick={copyWa}>{copied ? '✓ Copiado' : '💬 Copiar resumen para WhatsApp'}</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
